import React, { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Mail, Lock, Eye, EyeOff, ArrowRight, Sparkles } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import API from '../config/api';

const LoginPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const redirectParam = searchParams.get('redirect');
  const { login } = useAuth();

  // Email / Password Login States
  const [showPassword, setShowPassword] = useState(false);
  const [emailLoading, setEmailLoading] = useState(false);
  const [formData, setFormData] = useState({ email: '', password: '' });

  const handleGoogleLogin = () => {
    window.location.href = `${API}/auth/google`;
  };

  const handleEmailLogin = async (e) => {
    e.preventDefault();
    setEmailLoading(true);
    const result = await login(formData.email.trim(), formData.password);
    setEmailLoading(false);

    if (result.success) {
      toast.success('Welcome back!');
      if (result.user?.role === 'admin' && !redirectParam) {
        navigate('/admin');
      } else if (redirectParam) {
        navigate(redirectParam);
      } else {
        navigate('/dashboard');
      }
    } else {
      toast.error(result.error || 'Login failed. Please verify your credentials.');
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-orange-50 via-white to-amber-50 flex">
      {/* Left Side - Spiritual Himalayan Branding */}
      <div className="hidden lg:flex lg:w-1/2 bg-gradient-to-br from-[#FF7B00] to-[#D4AF37] p-12 flex-col justify-center relative overflow-hidden">
        <div className="absolute inset-0 bg-black/10"></div>
        <div className="relative z-10">
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8 }}
          >
            <div className="flex items-center space-x-3 mb-8">
              <div className="w-12 h-12 rounded-2xl bg-white/20 backdrop-blur-sm flex items-center justify-center border border-white/30">
                <Sparkles className="w-6 h-6 text-white" />
              </div>
              <span className="text-white font-heading text-2xl font-black">Paramparik Pooja</span>
            </div>

            <h1 className="text-4xl lg:text-5xl font-heading font-black text-white mb-6 leading-tight">
              Divine Blessings,
              <span className="block text-amber-200">Directly to Your Home</span>
            </h1>

            <p className="text-white/90 text-lg mb-8 leading-relaxed">
              Experience the ancient traditions of Himalayan shrines, energized rudrakshas, authentic Gangotri prasad, and sacred temple pujas.
            </p>

            <div className="space-y-4">
              <div className="flex items-center space-x-3 text-white/95">
                <div className="w-2.5 h-2.5 bg-amber-300 rounded-full"></div>
                <span className="font-medium">Easy puja booking with your name and WhatsApp number</span>
              </div>
              <div className="flex items-center space-x-3 text-white/95">
                <div className="w-2.5 h-2.5 bg-amber-300 rounded-full"></div>
                <span className="font-medium">Live video updates &amp; sankalp recording on WhatsApp</span>
              </div>
              <div className="flex items-center space-x-3 text-white/95">
                <div className="w-2.5 h-2.5 bg-amber-300 rounded-full"></div>
                <span className="font-medium">Secure Razorpay payment processing &amp; verified pandits</span>
              </div>
            </div>
          </motion.div>
        </div>

        {/* Decorative Orbs */}
        <div className="absolute top-20 right-20 w-40 h-40 bg-white/10 rounded-full blur-2xl"></div>
        <div className="absolute bottom-20 left-20 w-32 h-32 bg-amber-300/20 rounded-full blur-xl"></div>
      </div>

      {/* Right Side - Login Form */}
      <div className="flex-1 flex items-center justify-center p-6 sm:p-12">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.1 }}
          className="w-full max-w-md"
        >
          <div className="text-center mb-6">
            <Link to="/" className="inline-flex items-center space-x-2 mb-4">
              <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-[#FF7B00] to-[#D4AF37] flex items-center justify-center shadow-md">
                <span className="text-white font-heading font-black text-2xl">प</span>
              </div>
            </Link>

            <h1 className="font-heading text-2xl sm:text-3xl font-black text-[#1A202C] mb-1">
              Welcome to Paramparik
            </h1>
            <p className="text-gray-500 text-xs sm:text-sm">
              {redirectParam ? 'Sign in to complete your sacred booking' : 'Enter your details to access your account'}
            </p>
          </div>

          {/* ══════════════ TAB 2: EMAIL & PASSWORD LOGIN ══════════════ */}
          {(
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3 }}
            >
              <form onSubmit={handleEmailLogin} className="space-y-4">
                <div className="space-y-1.5">
                  <Label htmlFor="email" className="text-xs font-bold uppercase tracking-wider text-gray-700">
                    Email Address
                  </Label>
                  <div className="relative">
                    <Mail className="absolute left-3.5 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <Input
                      id="email"
                      type="email"
                      placeholder="Enter your registered email"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      className="pl-10 h-12 border-gray-200 rounded-xl focus:border-[#FF7B00] text-sm"
                      required
                    />
                  </div>
                </div>

                <div className="space-y-1.5">
                  <div className="flex justify-between items-center">
                    <Label htmlFor="password" className="text-xs font-bold uppercase tracking-wider text-gray-700">
                      Password
                    </Label>
                  </div>
                  <div className="relative">
                    <Lock className="absolute left-3.5 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <Input
                      id="password"
                      type={showPassword ? 'text' : 'password'}
                      placeholder="Enter your password"
                      value={formData.password}
                      onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                      className="pl-10 pr-10 h-12 border-gray-200 rounded-xl focus:border-[#FF7B00] text-sm"
                      required
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3.5 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-700"
                    >
                      {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>

                <Button
                  type="submit"
                  disabled={emailLoading}
                  className="w-full h-12 btn-saffron font-bold text-base rounded-xl shadow-lg hover:shadow-xl transition-all mt-2"
                >
                  {emailLoading ? (
                    <div className="flex items-center space-x-2">
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                      <span>Signing In...</span>
                    </div>
                  ) : (
                    <div className="flex items-center justify-center space-x-2">
                      <span>Sign In with Email</span>
                      <ArrowRight className="w-4 h-4" />
                    </div>
                  )}
                </Button>
              </form>
            </motion.div>
          )}

          {/* Divider */}
          <div className="relative my-6 text-center">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-gray-200"></div>
            </div>
            <span className="relative bg-[#FAFBFC] px-4 text-xs font-semibold uppercase tracking-wider text-gray-500">
              Or continue with
            </span>
          </div>

          {/* Google Sign In Button */}
          <button
            type="button"
            onClick={handleGoogleLogin}
            className="w-full h-12 flex items-center justify-center gap-3 bg-white hover:bg-gray-50 text-gray-700 font-semibold text-sm rounded-xl border border-gray-200 shadow-xs hover:shadow-md transition-all active:scale-[0.99] cursor-pointer"
            data-testid="google-login-btn"
          >
            <svg className="w-5 h-5" viewBox="0 0 24 24">
              <path
                fill="#4285F4"
                d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
              />
              <path
                fill="#34A853"
                d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
              />
              <path
                fill="#FBBC05"
                d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.06H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.94l2.85-2.22.81-.63z"
              />
              <path
                fill="#EA4335"
                d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.06l3.66 2.84c.87-2.6 3.3-4.52 6.16-4.52z"
              />
            </svg>
            <span>Sign in with Google</span>
          </button>

          <div className="mt-6 text-center">
            <p className="text-gray-600 text-xs">
              Need assistance?{' '}
              <a
                href="https://wa.me/918000000000"
                target="_blank"
                rel="noopener noreferrer"
                className="text-[#25D366] hover:underline font-bold"
              >
                Chat with Acharya Support on WhatsApp
              </a>
            </p>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default LoginPage;
